import React, { useMemo, useState } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  BarChart3,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import type { ModuleElement } from '@/types/filiere.types';
import type { ExamPeriods } from '../../services/examScheduleService';

interface RattrapageSchedulingProgressPanelProps {
  matieres: ModuleElement[];
  scheduledRattrapagesMap: Map<string, number>;
  groupName?: string;
  examPeriods: ExamPeriods | null;
}

type RattrapageStatus = 'NOT_SCHEDULED' | 'SCHEDULED';

interface ModuleRattrapageProgress {
  matiere: ModuleElement;
  rattrapageCount: number;
  status: RattrapageStatus;
}

export const RattrapageSchedulingProgressPanel: React.FC<RattrapageSchedulingProgressPanelProps> = ({
  matieres,
  scheduledRattrapagesMap,
  groupName,
  examPeriods,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate progress for each module
  const moduleProgress = useMemo((): ModuleRattrapageProgress[] => {
    return matieres.map((matiere) => {
      const rattrapageCount = scheduledRattrapagesMap.get(matiere.id) || 0;
      const status: RattrapageStatus = rattrapageCount > 0 ? 'SCHEDULED' : 'NOT_SCHEDULED';

      return {
        matiere,
        rattrapageCount,
        status,
      };
    });
  }, [matieres, scheduledRattrapagesMap]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalModules = moduleProgress.length;
    const modulesScheduled = moduleProgress.filter((m) => m.status === 'SCHEDULED').length;
    const modulesNotScheduled = moduleProgress.filter((m) => m.status === 'NOT_SCHEDULED').length;
    const totalRattrapages = moduleProgress.reduce((sum, m) => sum + m.rattrapageCount, 0);

    const percentage = totalModules > 0 ? (modulesScheduled / totalModules) * 100 : 0;

    return {
      totalModules,
      modulesScheduled,
      modulesNotScheduled,
      totalRattrapages,
      percentage,
    };
  }, [moduleProgress]);

  // Get rattrapage period info
  const rattrapagePeriodInfo = useMemo(() => {
    if (!examPeriods?.rattrapage1?.start || !examPeriods?.rattrapage1?.end) return null;
    return {
      start: new Date(examPeriods.rattrapage1.start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      end: new Date(examPeriods.rattrapage1.end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      deliberation: null, // Removed deliberation as it's not in the new type
    };
  }, [examPeriods]);

  const getStatusIcon = (status: RattrapageStatus) => {
    switch (status) {
      case 'SCHEDULED':
        return <CheckCircle className="w-4 h-4 text-amber-500" />;
      case 'NOT_SCHEDULED':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-l-lg shadow-lg p-2 hover:bg-gray-50 transition-colors z-40"
        title="Ouvrir le panneau de progression"
      >
        <div className="flex flex-col items-center gap-2">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
          <BarChart3 className="w-5 h-5 text-amber-600" />
          <span className="text-xs font-medium text-gray-600 writing-mode-vertical">
            Rattrapages
          </span>
        </div>
      </button>
    );
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Progression Rattrapages</h3>
            {groupName && <p className="text-xs text-gray-500">{groupName}</p>}
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Réduire le panneau"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Rattrapage Period Info */}
      {rattrapagePeriodInfo && (
        <div className="px-4 py-3 border-b border-gray-100 bg-amber-50">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-800">Période de rattrapage</span>
          </div>
          <p className="text-xs text-amber-700">
            {rattrapagePeriodInfo.start} - {rattrapagePeriodInfo.end}
          </p>
          {rattrapagePeriodInfo.deliberation && (
            <p className="text-[10px] text-amber-600 mt-0.5">
              Délibération: {rattrapagePeriodInfo.deliberation}
            </p>
          )}
        </div>
      )}

      {!rattrapagePeriodInfo && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-500">Période de rattrapage non configurée</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-3">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-600">Matières avec rattrapage</span>
            <span className="text-xs font-semibold text-gray-900">
              {summary.modulesScheduled}/{summary.totalModules}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all duration-300"
              style={{ width: `${summary.percentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {summary.totalRattrapages} rattrapage{summary.totalRattrapages !== 1 ? 's' : ''} planifié{summary.totalRattrapages !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 bg-amber-50 rounded-lg">
            <p className="text-lg font-bold text-amber-700">{summary.modulesScheduled}</p>
            <p className="text-xs text-amber-600">Planifié</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-700">{summary.modulesNotScheduled}</p>
            <p className="text-xs text-gray-600">Non planifié</p>
          </div>
        </div>
      </div>

      {/* Module List */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Matières ({summary.totalModules})
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {moduleProgress.map((item) => (
            <div
              key={item.matiere.id}
              className="px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.matiere.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.rattrapageCount > 0 ? (
                      <span className="text-xs text-amber-600">
                        {item.rattrapageCount} rattrapage{item.rattrapageCount > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Non planifié</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {moduleProgress.length === 0 && (
            <div className="px-4 py-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Aucune matière à planifier</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RattrapageSchedulingProgressPanel;
